import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiCpu, FiCheck, FiAlertCircle, FiClock } from 'react-icons/fi';
import { generateContent, getOpenAIHistory, publishGeneration } from '../../services/api';
import { format } from 'date-fns';

function ContentStudio() {
  const [prompt, setPrompt] = useState('');
  const [section, setSection] = useState('hero.headline');
  const [tone, setTone] = useState('professional');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedText, setGeneratedText] = useState('');
  const [generationId, setGenerationId] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const response = await getOpenAIHistory();
      setHistory(response.data.history);
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    setIsGenerating(true);
    setError('');
    setGeneratedText('');
    setGenerationId(null);

    try {
      const response = await generateContent(prompt, section, tone);
      setGeneratedText(response.data.generated_text);
      setGenerationId(response.data.generation_id);
      loadHistory(); // Refresh history
    } catch (err) {
      console.error('Generation error:', err);
      setError(err.response?.data?.detail || 'Failed to generate content');
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePublish = async (genId, targetSection) => {
    try {
      await publishGeneration(genId, targetSection);
      alert('Content published successfully!');
      loadHistory();
    } catch (error) {
      console.error('Publish error:', error);
      alert('Failed to publish content');
    }
  };

  return (
    <div data-testid="content-studio">
      <h1 className="text-3xl font-bold text-dark-900 mb-6">AI Content Studio</h1>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Generator */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-bold text-dark-900 mb-4">Generate Content</h2>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <FiAlertCircle className="text-red-600 mt-0.5 mr-3 flex-shrink-0" />
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-dark-900 mb-2">
                Section
              </label>
              <select
                value={section}
                onChange={(e) => setSection(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="hero.headline">Hero Headline</option>
                <option value="hero.subheadline">Hero Subheadline</option>
                <option value="programs">Programs</option>
                <option value="proof_points">Proof Points</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-dark-900 mb-2">
                Tone
              </label>
              <select
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="professional">Professional</option>
                <option value="technical">Technical</option>
                <option value="formal">Formal</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-dark-900 mb-2">
                Prompt
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows="4"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Describe what content you want to generate..."
              />
            </div>

            <button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="w-full flex items-center justify-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400"
              data-testid="generate-content-btn"
            >
              <FiCpu className="mr-2" />
              {isGenerating ? 'Generating...' : 'Generate Content'}
            </button>
          </div>

          {generatedText && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center text-green-800 font-semibold">
                  <FiCheck className="mr-2" />
                  Generated (Draft)
                </div>
                <button
                  onClick={() => handlePublish(generationId, section)}
                  className="px-4 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                  data-testid="publish-content-btn"
                >
                  Publish to Live
                </button>
              </div>
              <p className="text-dark-900 whitespace-pre-wrap">{generatedText}</p>
            </motion.div>
          )}
        </div>

        {/* History */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-bold text-dark-900 mb-4">Generation History</h2>

          {isLoadingHistory ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No generations yet
            </div>
          ) : (
            <div className="space-y-4 max-h-[600px] overflow-y-auto">
              {history.map((item) => (
                <div key={item._id} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-600">{item.section}</span>
                    <span className={`px-2 py-1 text-xs rounded ${
                      item.status === 'published'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {item.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2 italic">"{item.prompt}"</p>
                  <p className="text-sm text-dark-900 mb-3">{item.generated_text}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-xs text-gray-500">
                      <FiClock className="mr-1" />
                      {format(new Date(item.generated_at), 'MMM dd, yyyy HH:mm')}
                    </div>
                    {item.status === 'draft' && (
                      <button
                        onClick={() => handlePublish(item._id, item.section)}
                        className="px-3 py-1 bg-primary-600 text-white text-xs rounded hover:bg-primary-700 transition-colors"
                      >
                        Publish
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ContentStudio;